import React, { useState } from "react";
import { FileText, GraduationCap, Video, Plus, Trash2, LayoutDashboard, Upload, Film, CheckCircle2, Image as ImageIcon } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useToast } from "../hooks/use-toast";
import { STAGES } from "../mock";
import { uploadFileChunked, fileUrl } from "../uploads";
import RichTextEditor from "../components/RichTextEditor";

const TABS = [
  { id: "articulos", label: "Artículos", icon: FileText },
  { id: "cursos", label: "Cursos", icon: GraduationCap },
  { id: "sesiones", label: "Sesiones", icon: Video },
];

function Field({ label, children }) {
  return (
    <div>
      <label className="font-sans text-sm text-ink/70 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full font-body bg-cream border border-amber/40 rounded-xl px-4 py-3 outline-none focus:border-plum transition-colors";

const emptyCourse = { title: "", stage: "embarazo", description: "", duration: "" };

export default function Admin() {
  const store = useStore();
  const { toast } = useToast();
  const [tab, setTab] = useState("articulos");

  const [art, setArt] = useState({ title: "", stage: "embarazo", excerpt: "", content: "", cover: "/images/embarazo.png", readTime: 5 });
  const [artCover, setArtCover] = useState({ url: "", uploading: false, progress: 0 });
  const [editorKey, setEditorKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [ses, setSes] = useState({ title: "", stage: "embarazo", date: "", time: "", zoomLink: "", description: "" });

  // ---- Course form state ----
  const [course, setCourse] = useState(emptyCourse);
  const [cover, setCover] = useState({ url: "", uploading: false, progress: 0 });
  const [sections, setSections] = useState([]);
  const [savingCourse, setSavingCourse] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);

  const resetArticleForm = () => {
    setArt({ title: "", stage: "embarazo", excerpt: "", content: "", cover: "/images/embarazo.png", readTime: 5 });
    setArtCover({ url: "", uploading: false, progress: 0 });
    setEditingId(null);
    setEditorKey((k) => k + 1);
  };

  const startEditArticle = (a) => {
    setEditingId(a.id);
    setArt({
      title: a.title || "",
      stage: a.stage || "embarazo",
      excerpt: a.excerpt || "",
      content: a.content || "",
      cover: a.cover || "/images/embarazo.png",
      readTime: a.readTime || 5,
    });
    setArtCover({ url: "", uploading: false, progress: 0 });
    setEditorKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitArticle = async (e) => {
    e.preventDefault();
    if (artCover.uploading) {
      toast({ title: "Espera a que termine la subida de la portada" });
      return;
    }
    const plain = (art.content || "").replace(/<[^>]*>/g, "").trim();
    if (!plain) {
      toast({ title: "Escribe el contenido del artículo" });
      return;
    }
    try {
      const payload = { ...art, cover: artCover.url || art.cover };
      if (editingId) {
        await store.updateArticle(editingId, payload);
        toast({ title: "Artículo actualizado" });
      } else {
        await store.addArticle(payload);
        toast({ title: "Artículo publicado" });
      }
      resetArticleForm();
    } catch (err) {
      toast({ title: "Error al guardar", description: err.response?.data?.detail || "" });
    }
  };

  const handleArtCover = async (file) => {
    if (!file) return;
    setArtCover({ url: "", uploading: true, progress: 0 });
    try {
      const res = await uploadFileChunked(file, (p) => setArtCover((c) => ({ ...c, progress: p })));
      setArtCover({ url: res.url, uploading: false, progress: 100 });
    } catch {
      setArtCover({ url: "", uploading: false, progress: 0 });
      toast({ title: "Error al subir la portada" });
    }
  };

  const handleCover = async (file) => {
    if (!file) return;
    setCover({ url: "", uploading: true, progress: 0 });
    try {
      const res = await uploadFileChunked(file, (p) => setCover((c) => ({ ...c, progress: p })));
      setCover({ url: res.url, uploading: false, progress: 100 });
    } catch {
      setCover({ url: "", uploading: false, progress: 0 });
      toast({ title: "Error al subir la portada" });
    }
  };

  const addSection = () =>
    setSections((prev) => [...prev, { title: "", url: "", filename: "", type: "", downloadable: false, uploading: false, progress: 0 }]);

  const updateSection = (i, patch) =>
    setSections((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const removeSection = (i) => setSections((prev) => prev.filter((_, idx) => idx !== i));

  const handleSectionFile = async (i, file) => {
    if (!file) return;
    updateSection(i, { uploading: true, progress: 0 });
    try {
      const res = await uploadFileChunked(file, (p) => updateSection(i, { progress: p }));
      updateSection(i, { url: res.url, filename: res.filename, type: res.type, uploading: false, progress: 100 });
    } catch {
      updateSection(i, { uploading: false, progress: 0 });
      toast({ title: "Error al subir el archivo" });
    }
  };

  const submitCourse = async (e) => {
    e.preventDefault();
    if (sections.some((s) => s.uploading) || cover.uploading) {
      toast({ title: "Espera a que terminen las subidas" });
      return;
    }
    setSavingCourse(true);
    try {
      const payload = {
        ...course,
        cover: cover.url || course.cover || undefined,
        sections: sections
          .filter((s) => s.title && s.url)
          .map((s) => ({ id: s.id, title: s.title, url: s.url, filename: s.filename, type: s.type, downloadable: s.downloadable })),
      };
      if (editingCourseId) {
        await store.updateCourse(editingCourseId, payload);
        toast({ title: "Curso actualizado" });
      } else {
        await store.addCourse(payload);
        toast({ title: "Curso creado" });
      }
      resetCourseForm();
    } catch (err) {
      toast({ title: "Error al guardar el curso", description: err.response?.data?.detail || "" });
    } finally {
      setSavingCourse(false);
    }
  };

  const resetCourseForm = () => {
    setCourse(emptyCourse);
    setCover({ url: "", uploading: false, progress: 0 });
    setSections([]);
    setEditingCourseId(null);
  };

  const startEditCourse = (c) => {
    setEditingCourseId(c.id);
    setCourse({
      title: c.title || "",
      stage: c.stage || "embarazo",
      description: c.description || "",
      duration: c.duration && c.duration !== "—" ? c.duration : "",
      cover: c.cover || "",
    });
    setCover({ url: "", uploading: false, progress: 0 });
    setSections(
      (c.sections || []).map((s) => ({
        id: s.id,
        title: s.title || "",
        url: s.url || "",
        filename: s.filename || "",
        type: s.type || "",
        downloadable: !!s.downloadable,
        uploading: false,
        progress: s.url ? 100 : 0,
      }))
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitSession = async (e) => {
    e.preventDefault();
    try {
      await store.addSession(ses);
      setSes({ title: "", stage: "embarazo", date: "", time: "", zoomLink: "", description: "" });
      toast({ title: "Sesión programada" });
    } catch (err) {
      toast({ title: "Error al programar", description: err.response?.data?.detail || "" });
    }
  };

  return (
    <div className="animate-fade-in">
      <section className="bg-plum text-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
          <span className="flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-amber">
            <LayoutDashboard size={16} /> Panel de administración
          </span>
          <h1 className="font-serif text-4xl md:text-5xl mt-3">Gestiona tu comunidad</h1>
          <p className="font-body text-cream/75 mt-2">
            Sube artículos, crea cursos con sus secciones y programa las sesiones grupales por Zoom.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 font-sans text-sm px-5 py-2.5 rounded-full transition-colors ${
                tab === t.id ? "bg-plum text-cream" : "bg-white text-ink/70 border border-amber/40 hover:border-plum/40"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {/* ============ ARTICULOS ============ */}
        {tab === "articulos" && (
          <div className="grid lg:grid-cols-[420px_1fr] gap-8">
            <form onSubmit={submitArticle} className="bg-white rounded-2xl shadow-sm border border-amber/30 p-7 space-y-4 h-fit">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-plum">{editingId ? "Editar artículo" : "Nuevo artículo"}</h3>
                {editingId && (
                  <button type="button" onClick={resetArticleForm} className="font-sans text-sm text-ink/50 hover:text-terracotta transition-colors">
                    Cancelar
                  </button>
                )}
              </div>
              <Field label="Título">
                <input required value={art.title} onChange={(e) => setArt({ ...art, title: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Etapa">
                <select value={art.stage} onChange={(e) => setArt({ ...art, stage: e.target.value, cover: `/images/${e.target.value}.png` })} className={inputCls}>
                  {STAGES.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </Field>
              <Field label="Resumen">
                <textarea required rows={2} value={art.excerpt} onChange={(e) => setArt({ ...art, excerpt: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Contenido">
                <RichTextEditor
                  key={editorKey}
                  value={art.content}
                  onChange={(html) => setArt((prev) => ({ ...prev, content: html }))}
                />
              </Field>

              <Field label="Tiempo de lectura (min)">
                <input type="number" min={1} max={120} value={art.readTime} onChange={(e) => setArt({ ...art, readTime: Number(e.target.value) })} className={inputCls} />
              </Field>
              <Field label="Imagen de portada (opcional)">
                <label className="flex items-center gap-3 cursor-pointer bg-cream border border-dashed border-amber/60 rounded-xl px-4 py-3 hover:border-plum transition-colors">
                  <ImageIcon size={20} className="text-plum" />
                  <span className="font-body text-sm text-ink/60">
                    {artCover.uploading ? `Subiendo... ${artCover.progress}%` : artCover.url ? "Portada subida ✓ (cambiar)" : editingId ? "Cambiar imagen de portada" : "Seleccionar imagen (si no, se usa la de la etapa)"}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleArtCover(e.target.files[0])} />
                </label>
                {(artCover.url || editingId) && !artCover.uploading && (
                  <img src={fileUrl(artCover.url || art.cover)} alt="portada" className="mt-3 w-full h-36 object-cover rounded-xl" />
                )}
              </Field>
              <button className="w-full flex items-center justify-center gap-2 bg-plum text-cream font-sans py-3 rounded-xl hover:bg-[#684d89] transition-colors">
                <Plus size={18} /> {editingId ? "Guardar cambios" : "Publicar artículo"}
              </button>
            </form>
            <div className="space-y-3">
              {store.articles.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl border border-amber/30 p-4 flex items-center gap-4">
                  <img src={fileUrl(a.cover)} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1">
                    <span className="font-sans text-[10px] uppercase tracking-[0.15em] text-terracotta">{a.stage}</span>
                    <h4 className="font-serif text-xl text-plum leading-tight">{a.title}</h4>
                  </div>
                  <button onClick={() => startEditArticle(a)} className="font-sans text-sm text-plum border border-plum/30 rounded-full px-4 py-1.5 hover:bg-plum/5 transition-colors">
                    Editar
                  </button>
                  <button onClick={() => { store.deleteArticle(a.id); if (editingId === a.id) resetArticleForm(); toast({ title: "Artículo eliminado" }); }} className="text-ink/40 hover:text-destructive transition-colors p-2">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ CURSOS ============ */}
        {tab === "cursos" && (
          <div className="grid lg:grid-cols-[460px_1fr] gap-8">
            <form onSubmit={submitCourse} className="bg-white rounded-2xl shadow-sm border border-amber/30 p-7 space-y-5 h-fit">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-plum">{editingCourseId ? "Editar curso" : "Nuevo curso"}</h3>
                {editingCourseId && (
                  <button type="button" onClick={resetCourseForm} data-testid="cancel-edit-course-btn" className="font-sans text-sm text-ink/50 hover:text-terracotta transition-colors">
                    Cancelar
                  </button>
                )}
              </div>

              <Field label="Título">
                <input required value={course.title} onChange={(e) => setCourse({ ...course, title: e.target.value })} className={inputCls} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Etapa">
                  <select value={course.stage} onChange={(e) => setCourse({ ...course, stage: e.target.value })} className={inputCls}>
                    {STAGES.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </select>
                </Field>
                <Field label="Duración (opcional)">
                  <input value={course.duration} onChange={(e) => setCourse({ ...course, duration: e.target.value })} placeholder="3 h 45 min" className={inputCls} />
                </Field>
              </div>
              <Field label="Descripción">
                <textarea required rows={3} value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })} className={inputCls} />
              </Field>

              {/* COVER */}
              <Field label="Imagen de portada">
                <label className="flex items-center gap-3 cursor-pointer bg-cream border border-dashed border-amber/60 rounded-xl px-4 py-3 hover:border-plum transition-colors">
                  <ImageIcon size={20} className="text-plum" />
                  <span className="font-body text-sm text-ink/60">
                    {cover.uploading ? `Subiendo... ${cover.progress}%` : cover.url ? "Portada subida ✓ (cambiar)" : editingCourseId ? "Cambiar imagen de portada" : "Seleccionar imagen"}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleCover(e.target.files[0])} />
                </label>
                {(cover.url || (editingCourseId && course.cover)) && !cover.uploading && (
                  <img src={fileUrl(cover.url || course.cover)} alt="portada" className="mt-3 w-full h-36 object-cover rounded-xl" />
                )}
              </Field>

              {/* SECTIONS */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-sans text-sm text-ink/70">Secciones del curso</label>
                  <button type="button" onClick={addSection} className="flex items-center gap-1 text-sm font-sans text-terracotta hover:text-plum transition-colors">
                    <Plus size={15} /> Añadir sección
                  </button>
                </div>
                <div className="space-y-3">
                  {sections.length === 0 && (
                    <p className="font-body text-sm text-ink/45">Añade tantas secciones como tenga el curso.</p>
                  )}
                  {sections.map((s, i) => (
                    <div key={i} className="border border-amber/40 rounded-xl p-4 bg-cream/50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-sans text-xs uppercase tracking-[0.12em] text-terracotta">Sección {i + 1}</span>
                        <button type="button" onClick={() => removeSection(i)} className="text-ink/40 hover:text-destructive transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <input
                        value={s.title}
                        onChange={(e) => updateSection(i, { title: e.target.value })}
                        placeholder="Título de la sección"
                        className="w-full font-body bg-white border border-amber/40 rounded-lg px-3 py-2 text-sm outline-none focus:border-plum"
                      />
                      <label className="flex items-center gap-2 cursor-pointer bg-white border border-dashed border-amber/60 rounded-lg px-3 py-2 hover:border-plum transition-colors">
                        {s.type === "video" ? <Film size={16} className="text-plum" /> : <Upload size={16} className="text-plum" />}
                        <span className="font-body text-xs text-ink/60 truncate">
                          {s.uploading ? `Subiendo... ${s.progress}%` : s.url ? (s.filename || "Archivo subido ✓") : "Subir vídeo, PDF, imagen o audio"}
                        </span>
                        <input type="file" className="hidden" onChange={(e) => handleSectionFile(i, e.target.files[0])} />
                      </label>
                      {s.url && !s.uploading && (
                        <span className="flex items-center gap-1.5 text-xs font-body text-plum"><CheckCircle2 size={14} /> {s.type}</span>
                      )}
                      <label className="flex items-center gap-2 font-body text-sm text-ink/70 cursor-pointer">
                        <input type="checkbox" checked={s.downloadable} onChange={(e) => updateSection(i, { downloadable: e.target.checked })} className="w-4 h-4 accent-plum" />
                        Permitir descarga de este archivo
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <button disabled={savingCourse} data-testid="submit-course-btn" className="w-full flex items-center justify-center gap-2 bg-plum text-cream font-sans py-3 rounded-xl hover:bg-[#684d89] transition-colors disabled:opacity-60">
                <Plus size={18} /> {savingCourse ? "Guardando..." : editingCourseId ? "Guardar cambios" : "Crear curso"}
              </button>
            </form>

            <div className="space-y-3">
              {store.courses.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl border border-amber/30 p-4 flex items-center gap-4">
                  <img src={fileUrl(c.cover)} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1">
                    <span className="font-sans text-[10px] uppercase tracking-[0.15em] text-terracotta">{c.stage}</span>
                    <h4 className="font-serif text-xl text-plum leading-tight">{c.title}</h4>
                    <p className="font-body text-xs text-ink/50 mt-0.5">{(c.sections?.length || c.lessons || 0)} secciones</p>
                  </div>
                  <button onClick={() => startEditCourse(c)} data-testid={`edit-course-btn-${c.id}`} className="font-sans text-sm text-plum border border-plum/30 rounded-full px-4 py-1.5 hover:bg-plum/5 transition-colors">
                    Editar
                  </button>
                  <button onClick={() => { store.deleteCourse(c.id); if (editingCourseId === c.id) resetCourseForm(); toast({ title: "Curso eliminado" }); }} className="text-ink/40 hover:text-destructive transition-colors p-2">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ SESIONES ============ */}
        {tab === "sesiones" && (
          <div className="grid lg:grid-cols-[420px_1fr] gap-8">
            <form onSubmit={submitSession} className="bg-white rounded-2xl shadow-sm border border-amber/30 p-7 space-y-4 h-fit">
              <h3 className="font-serif text-2xl text-plum">Nueva sesión Zoom</h3>
              <Field label="Título">
                <input required value={ses.title} onChange={(e) => setSes({ ...ses, title: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Etapa">
                <select value={ses.stage} onChange={(e) => setSes({ ...ses, stage: e.target.value })} className={inputCls}>
                  {STAGES.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Fecha">
                  <input required type="date" value={ses.date} onChange={(e) => setSes({ ...ses, date: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Hora">
                  <input required value={ses.time} onChange={(e) => setSes({ ...ses, time: e.target.value })} placeholder="19:00" className={inputCls} />
                </Field>
              </div>
              <Field label="Enlace de Zoom">
                <input required value={ses.zoomLink} onChange={(e) => setSes({ ...ses, zoomLink: e.target.value })} placeholder="https://zoom.us/j/..." className={inputCls} />
              </Field>
              <Field label="Descripción">
                <textarea rows={2} value={ses.description} onChange={(e) => setSes({ ...ses, description: e.target.value })} className={inputCls} />
              </Field>
              <button className="w-full flex items-center justify-center gap-2 bg-plum text-cream font-sans py-3 rounded-xl hover:bg-[#684d89] transition-colors">
                <Plus size={18} /> Programar sesión
              </button>
            </form>
            <div className="space-y-3">
              {store.sessions.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl border border-amber/30 p-4 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-plum/10 flex items-center justify-center text-plum shrink-0">
                    <Video size={24} />
                  </div>
                  <div className="flex-1">
                    <span className="font-sans text-[10px] uppercase tracking-[0.15em] text-terracotta">{s.stage}</span>
                    <h4 className="font-serif text-xl text-plum leading-tight">{s.title}</h4>
                    <p className="font-body text-xs text-ink/50 mt-0.5">{s.date} · {s.time}</p>
                  </div>
                  <button onClick={() => { store.deleteSession(s.id); toast({ title: "Sesión eliminada" }); }} className="text-ink/40 hover:text-destructive transition-colors p-2">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
